import { MAPPING_ATYPE } from '../../catalog/mapping';
import { RCU_TOP_TYPE } from '../../catalog/protocol';
import { ISSUE, ISSUE_TARGET, configIssue } from '../../catalog/issue';
import type { ConfigIssue } from '../../catalog/issue';
import type { Result } from '../../result';
import type {
  RcuAddLedMappingRequest,
  RcuDaliLedEntry,
  RcuDeleteEntry,
  RcuDocument,
  RcuLedEntry,
  RcuLedSelector,
  RcuMapEraseEntry,
  RcuRestartEntry,
  RcuSceneDocumentEntry,
  RcuSceneEntry,
  RcuTrigger,
  RcuWifiConfigEntry,
} from '../../wire';
import { ledSelectorIssue, triggerIssue, validated } from './validate';

/**
 * Scene and LED mappings, plus the four configuration objects that ride in an
 * add-array: `delete`, `maperase`, `restart` and the legacy `wificonfig`.
 *
 * Every one of these is durable — it is written to the RCU's NVS — so the
 * console confirms before sending. Two of them are also destructive:
 * `maperase` erases the mapping namespace (legacy Wi-Fi credentials included)
 * and `wificonfig` overwrites stored credentials before rebooting into them.
 */

const emptyRequest = (label: string): ConfigIssue =>
  configIssue(ISSUE.COMMAND_FIELD_OUT_OF_RANGE, { kind: ISSUE_TARGET.RCU }, `${label} list is empty`);

/** Writes one or more entries in a single request. */
export const addMappings = (entries: readonly RcuSceneDocumentEntry[]): Result<RcuDocument> =>
  validated(
    { type: RCU_TOP_TYPE.ADD_MAPPING, data: entries },
    [
      entries.length === 0 ? emptyRequest('Mapping') : null,
      ...entries.flatMap((entry) => ('x' in entry ? [triggerIssue('Trigger', entry.x)] : [])),
    ],
  );

export const addScene = (entry: RcuSceneEntry): Result<RcuDocument> => addMappings([entry]);

/** Removes one scene. A trigger that matches nothing is a silent no-op. */
export const deleteScene = (x: RcuTrigger): Result<RcuDocument> => {
  const entry: RcuDeleteEntry = { atype: MAPPING_ATYPE.DELETE, x };
  return validated({ type: RCU_TOP_TYPE.ADD_MAPPING, data: [entry] }, [triggerIssue('Trigger', x)]);
};

/** **Destructive:** erases the whole mapping namespace, then restarts. */
export const eraseMappingsAndRestart = (): Result<RcuDocument> => {
  const entry: RcuMapEraseEntry = { atype: MAPPING_ATYPE.MAP_ERASE };
  return validated({ type: RCU_TOP_TYPE.ADD_MAPPING, data: [entry] });
};

/** **Disruptive:** restarts the RCU two seconds later, with no ack. */
export const restart = (): Result<RcuDocument> => {
  const entry: RcuRestartEntry = { atype: MAPPING_ATYPE.RESTART };
  return validated({ type: RCU_TOP_TYPE.ADD_MAPPING, data: [entry] });
};

/**
 * **Disruptive:** writes legacy Wi-Fi credentials and reboots into them. This is
 * not the normal provisioning path, and the parser logs incoming JSON — so the
 * console keeps the password out of the event feed and the payload preview.
 */
export const wifiConfig = (ssid: string, password: string): Result<RcuDocument> => {
  const entry: RcuWifiConfigEntry = { atype: MAPPING_ATYPE.WIFI_CONFIG, ssid, password };
  return validated({ type: RCU_TOP_TYPE.ADD_MAPPING, data: [entry] });
};

/** Reads stored scenes; a trigger with no mapping is simply absent from the reply. */
export const readMappings = (triggers: readonly RcuTrigger[]): Result<RcuDocument> =>
  validated(
    { type: RCU_TOP_TYPE.READ_MAPPING, data: triggers.map((x) => ({ x })) },
    [
      triggers.length === 0 ? emptyRequest('Trigger') : null,
      ...triggers.map((x) => triggerIssue('Trigger', x)),
    ],
  );

/** Erases the named scenes. The full erase is the available way to remove them all. */
export const eraseMappings = (triggers: readonly RcuTrigger[]): Result<RcuDocument> =>
  validated(
    { type: RCU_TOP_TYPE.ERASE_MAPPING, data: triggers.map((x) => ({ x })) },
    [
      triggers.length === 0 ? emptyRequest('Trigger') : null,
      ...triggers.map((x) => triggerIssue('Trigger', x)),
    ],
  );

/**
 * **Destructive:** `nvs_erase_all` on the mapping namespace. LED, DALI and
 * legacy Wi-Fi values stored there go too. It is not a factory reset.
 */
export const eraseAllMappings = (): Result<RcuDocument> =>
  validated({ type: RCU_TOP_TYPE.ERASE_ALL_MAPPINGS, data: {} });

/** Writes relay or DIN LED acknowledgement mappings. */
export const addLedMappings = (
  entries: readonly (RcuLedEntry | RcuDaliLedEntry)[],
): Result<RcuDocument> =>
  validated(
    { type: RCU_TOP_TYPE.ADD_LED_MAPPING, data: entries },
    [
      entries.length === 0 ? emptyRequest('LED mapping') : null,
      ...entries.map((entry) => ledSelectorIssue('Selector', entry.x)),
    ],
  );

/** Writes a DALI LED mapping, which no read endpoint can retrieve afterwards. */
export const addDaliLedMapping = (entry: RcuDaliLedEntry): Result<RcuDocument> =>
  addLedMappings([entry]);

export const readLedMappings = (selectors: readonly RcuLedSelector[]): Result<RcuDocument> => {
  const request: RcuDocument = {
    type: RCU_TOP_TYPE.READ_LED_MAPPING,
    data: selectors.map((x) => ({ x })),
  };
  return validated(request, [
    selectors.length === 0 ? emptyRequest('Selector') : null,
    ...selectors.map((x) => ledSelectorIssue('Selector', x)),
  ]);
};

/**
 * Erases selected LED mappings. The firmware builds a longer NVS key here than
 * when saving, so this fails for every valid selector; the console offers the
 * full erase instead and disables this one.
 */
export const eraseLedMappings = (selectors: readonly RcuLedSelector[]): Result<RcuDocument> =>
  validated(
    { type: RCU_TOP_TYPE.ERASE_LED_MAPPING, data: selectors.map((x) => ({ x })) },
    [
      selectors.length === 0 ? emptyRequest('Selector') : null,
      ...selectors.map((x) => ledSelectorIssue('Selector', x)),
    ],
  );

/** The add-array request type, exported for the console's LED editor. */
export type { RcuAddLedMappingRequest };
